import { createContext, ReactNode, useContext } from "react";

import type { SourcesV2 } from "./SelectSource";

/**
 * Opt-in React context that lets Smart-Table-aware wrappers thread a richer
 * `SourcesV2` shape (typed items + optional `sheets` two-layer group) to
 * every nested `SelectSource` without touching each block's source-selector
 * call-site.
 *
 * ## Why a context (and not a prop)?
 *
 * `SelectSource` is reached through several hop-points — `SingleSourceSelector`,
 * `DoubleSourceSelector`, `ArraySourceSelector`, `InputVariableRow`, and a
 * handful of ad-hoc inline uses in individual blocks. Every one of those
 * call-sites currently takes the legacy `Record<SourceType, string[]>` shape
 * via a `sources` prop, and many of them are consumed by non-Smart-Table
 * features (evals, workflows, MCP, etc.) where the cross-sheet concept
 * doesn't apply.
 *
 * Threading `sourcesV2` as a prop would require touching all of those
 * call-sites and every block that forwards `sources` down. A context lets
 * the Smart-Table sidebar set the richer shape **once**, at the top of the
 * subtree, and `SelectSource` picks it up transparently. Non-Smart-Table
 * call-sites don't install a provider and keep rendering exactly as before.
 *
 * ## Layering with the `sourcesV2` prop
 *
 * `SelectSource` still accepts a `sourcesV2` prop directly. When both a
 * prop and a context value are present, the prop wins — this matches the
 * usual "explicit beats implicit" convention and lets individual
 * Smart-Table blocks (e.g. the Composition block) render a **scoped**
 * `SourcesV2` (for instance, columns for a single chosen sheet) without
 * fighting the workspace-wide context installed by the sidebar.
 *
 * ## What the context does NOT replace
 *
 * The legacy `sources={...}` prop is still the persistence-layer input.
 * This context is purely a display-time enhancement so the dropdown can
 * render composition sources. Payload construction (title → dependency id)
 * still flows through the block-store's `allSources` channel and the
 * sidebar's `buildDependencyPayloadFromConfig`.
 */
const SourcesV2Context = createContext<SourcesV2 | null>(null);

/**
 * Wrap a subtree to expose a `SourcesV2` shape to every descendant
 * `SelectSource`. Intended to be installed at the top of the Smart-Table
 * `ColumnConfigSidebar` once the workspace-wide composition discovery
 * response is available.
 *
 * When `value` is nullish the provider still mounts (so children always
 * see a provider) but `useSourcesV2()` returns `null`, which downstream
 * call-sites use as a discriminator to fall back to the legacy shape.
 */
export const SourcesV2Provider = ({
  value,
  children,
}: {
  value: SourcesV2 | null | undefined;
  children: ReactNode;
}) => <SourcesV2Context.Provider value={value ?? null}>{children}</SourcesV2Context.Provider>;

/**
 * Read the ambient `SourcesV2` shape, if any. Returns `null` outside of a
 * `SourcesV2Provider`, which `SelectSource` treats as a signal to use the
 * legacy `Record<SourceType, string[]>` prop path.
 *
 * The nullable return is intentional: it lets non-Smart-Table callers
 * (evals, workflows, etc.) stay on the legacy render path without any
 * changes, while Smart-Table-aware callers can install the provider once
 * and let every nested source picker pick it up.
 */
export const useSourcesV2 = (): SourcesV2 | null => useContext(SourcesV2Context);
